import { useContext, useState, useEffect } from 'react'
import {
    Form,
    Upload,
    Button,
    InputNumber,
    Input,
    Space,
    Typography,
    Card,
    Row,
    Col,
    Divider,
    Tooltip,
} from 'antd'
import { UploadOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { AppContext } from '../../context/AppContext'
import { SERVER } from '../../network/API'
import { qrOverlayPercentStyle } from '../../utils/qrOverlay'

const { Text, Title } = Typography

const STORAGE_URI = '/storage/communities'
const REQUEST_URI = '/files/communities'

const DEFAULT_ICON_GRADIENT = {
    angle: 105,
    stops: [
        { color: '#98F183', offset: 0 },
        { color: '#11BF67', offset: 100 },
    ],
}

const DEFAULT_ICON_HEAD_GRADIENT = {
    angle: 105,
    stops: [
        { color: '#98F183', offset: 0 },
        { color: '#11BF67', offset: 100 },
    ],
}

const DEFAULT_TEXT_GRADIENT = {
    angle: 105,
    stops: [
        { color: '#98F183', offset: 0 },
        { color: '#11BF67', offset: 100 },
    ],
}

const DEFAULT_BG_HFIVE_GRADIENT = {
    angle: 105,
    stops: [
        { color: '#98F183', offset: 0 },
        { color: '#11BF67', offset: 100 },
    ],
}

const ICON_PREVIEW_SIZE = 200
const ICON_HEAD_PREVIEW_SIZE = 72
const TEXT_PREVIEW_SIZE = 40

const normalizeOffset = (offset) => {
    if (typeof offset === 'number' && !Number.isNaN(offset)) {
        return Math.min(100, Math.max(0, offset))
    }
    if (typeof offset === 'string') {
        const parsed = parseFloat(offset)
        if (!Number.isNaN(parsed)) {
            return Math.min(100, Math.max(0, parsed))
        }
    }
    return 0
}

const buildLinearGradient = ({ angle, stops }) => {
    const stopCss = (stops || [])
        .map((stop) => `${stop.color} ${normalizeOffset(stop.offset)}%`)
        .join(', ')
    return `linear-gradient(${angle}deg, ${stopCss})`
}

const parseLinearGradient = (value, fallback) => {
    if (!value || typeof value !== 'string') {
        return {
            angle: fallback.angle,
            stops: fallback.stops.map((stop) => ({ ...stop })),
        }
    }

    const match = value
        .trim()
        .match(/^linear-gradient\(\s*(-?[\d.]+)deg\s*,\s*(.+)\s*\)$/i)

    if (!match) {
        return {
            angle: fallback.angle,
            stops: fallback.stops.map((stop) => ({ ...stop })),
        }
    }

    const stops = []
    const stopRegex =
        /(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))\s*(-?[\d.]+%|[\d.]+)?/g
    let stopMatch
    while ((stopMatch = stopRegex.exec(match[2])) !== null) {
        stops.push({
            color: stopMatch[1],
            offset: normalizeOffset(stopMatch[2] || 0),
        })
    }

    if (stops.length < 2) {
        return {
            angle: fallback.angle,
            stops: fallback.stops.map((stop) => ({ ...stop })),
        }
    }

    stops.forEach((stop, index) => {
        if (stop.offset == null || Number.isNaN(stop.offset)) {
            stop.offset = (index / (stops.length - 1)) * 100
        }
    })

    return {
        angle: parseFloat(match[1]),
        stops,
    }
}

const getUploadFileList = (value) => {
    if (!value) {
        return []
    }

    let files = value?.fileList ? value.fileList : value
    if (!Array.isArray(files)) {
        files = [files]
    }

    return files
        .filter(Boolean)
        .map((file, index) => {
            if (file.uid || file.status) {
                return file
            }

            const response = file.response || file
            return {
                uid: `-${index + 1}`,
                name: response.name || response.uri || `file-${index}`,
                status: 'done',
                thumbUrl: response.uri
                    ? `${SERVER.baseURL}${STORAGE_URI}/${response.uri}`
                    : undefined,
                response,
            }
        })
}

const getPreviewUrl = (value) => {
    if (!value) {
        return null
    }

    const file = Array.isArray(value)
        ? value[0]
        : value?.fileList
        ? value.fileList[0]
        : value

    if (!file) {
        return null
    }

    if (file.thumbUrl) {
        return file.thumbUrl
    }

    if (file.url) {
        return file.url
    }

    const response = file.response || file
    if (response?.uri) {
        return `${SERVER.baseURL}${STORAGE_URI}/${response.uri}`
    }

    if (file.uri) {
        return `${SERVER.baseURL}${STORAGE_URI}/${file.uri}`
    }

    if (file.originFileObj instanceof Blob) {
        return URL.createObjectURL(file.originFileObj)
    }

    return null
}

const GradientStrip = ({ gradient }) => (
    <div
        style={{
            height: 10,
            borderRadius: 6,
            backgroundImage: gradient,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
            border: '1px solid #f0f0f0',
            marginBottom: 10,
        }}
    />
)

const HEX_COLOR_RE = /^#?[0-9a-fA-F]{6}$/

const normalizeHexColor = (value) => {
    if (!value || typeof value !== 'string') {
        return null
    }
    const trimmed = value.trim()
    if (!HEX_COLOR_RE.test(trimmed)) {
        return null
    }
    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
    return withHash.toUpperCase()
}

const toSwatchColor = (color) =>
    /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#000000'

const SVG_SHAPE_TAGS = 'path|circle|rect|ellipse|polygon|polyline|line'

const angleToSvgCoords = (angleDeg) => {
    const angle = ((angleDeg % 360) + 360) % 360
    const rad = (angle * Math.PI) / 180
    const x = Math.sin(rad)
    const y = -Math.cos(rad)
    return {
        x1: Number((0.5 - x / 2).toFixed(4)),
        y1: Number((0.5 - y / 2).toFixed(4)),
        x2: Number((0.5 + x / 2).toFixed(4)),
        y2: Number((0.5 + y / 2).toFixed(4)),
    }
}

const buildSvgGradientDefs = (gradientId, gradient) => {
    const coords = angleToSvgCoords(gradient.angle)
    const stopsXml = (gradient.stops || [])
        .map(
            (stop) =>
                `<stop offset="${
                    typeof stop.offset === 'number'
                        ? `${normalizeOffset(stop.offset)}%`
                        : stop.offset
                }" stop-color="${stop.color}" />`
        )
        .join('')
    return `<linearGradient id="${gradientId}" gradientUnits="objectBoundingBox" x1="${coords.x1}" y1="${coords.y1}" x2="${coords.x2}" y2="${coords.y2}">${stopsXml}</linearGradient>`
}

const parseSvgViewportSize = (svgContent) => {
    const viewBoxMatch = svgContent.match(
        /viewBox\s*=\s*["']\s*([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s*["']/i
    )
    if (viewBoxMatch) {
        const width = Math.abs(parseFloat(viewBoxMatch[3]))
        const height = Math.abs(parseFloat(viewBoxMatch[4]))
        if (width > 0 && height > 0) {
            return { width, height }
        }
    }

    const widthMatch = svgContent.match(/\bwidth\s*=\s*["']\s*([-\d.]+)/i)
    const heightMatch = svgContent.match(/\bheight\s*=\s*["']\s*([-\d.]+)/i)
    if (widthMatch && heightMatch) {
        const width = Math.abs(parseFloat(widthMatch[1]))
        const height = Math.abs(parseFloat(heightMatch[1]))
        if (width > 0 && height > 0) {
            return { width, height }
        }
    }

    return null
}

const normalizeStrokePercent = (percent) => {
    const value = Number(percent)
    if (!Number.isFinite(value)) {
        return 2
    }
    return Math.min(20, Math.max(0.1, value))
}

const resolveStrokeWidth = (svgContent, percent) => {
    const normalizedPercent = normalizeStrokePercent(percent)
    const viewport = parseSvgViewportSize(svgContent)
    if (!viewport) {
        return 2
    }
    return Math.max(
        2,
        Math.round(
            Math.min(viewport.width, viewport.height) * (normalizedPercent / 100)
        )
    )
}

const applyGradientToSvg = (
    svgContent,
    cssGradient,
    strokeColor = null,
    strokePercent = null
) => {
    const gradient = parseLinearGradient(cssGradient, DEFAULT_ICON_HEAD_GRADIENT)
    if (!gradient?.stops?.length) {
        throw new Error('Invalid icon gradient')
    }

    const gradientId = `community-gradient-${Math.random()
        .toString(36)
        .slice(2, 10)}`
    let svg = svgContent

    if (!/<svg[\s>]/i.test(svg)) {
        throw new Error('Invalid SVG source content')
    }

    if (!/xmlns=/.test(svg)) {
        svg = svg.replace(/<svg/i, '<svg xmlns="http://www.w3.org/2000/svg"')
    }

    const gradientXml = buildSvgGradientDefs(gradientId, gradient)
    if (/<defs[\s>]/i.test(svg)) {
        svg = svg.replace(/<defs([^>]*)>/i, `<defs$1>${gradientXml}`)
    } else {
        svg = svg.replace(/<svg([^>]*)>/i, `<svg$1><defs>${gradientXml}</defs>`)
    }

    const normalizedStroke = normalizeHexColor(strokeColor)
    const strokeWidth = normalizedStroke
        ? resolveStrokeWidth(svg, strokePercent)
        : null

    const tagRegex = new RegExp(
        `<(${SVG_SHAPE_TAGS})(\\s[^>]*?)?(\\s*/?)>`,
        'gi'
    )
    svg = svg.replace(tagRegex, (full, tag, attrs = '', closer = '') => {
        let nextAttrs = attrs || ''
        nextAttrs = nextAttrs.replace(/\sfill=(["'])(.*?)\1/gi, '')
        nextAttrs = nextAttrs.replace(/\sstroke=(["'])(.*?)\1/gi, '')
        nextAttrs = nextAttrs.replace(/\sstroke-width=(["'])(.*?)\1/gi, '')
        nextAttrs = nextAttrs.replace(/\spaint-order=(["'])(.*?)\1/gi, '')
        nextAttrs = nextAttrs.replace(/\sstyle=(["'])(.*?)\1/gi, (m, q, style) => {
            const cleaned = style
                .replace(/fill\s*:\s*[^;]+;?/gi, '')
                .replace(/stroke(?:-width)?\s*:\s*[^;]+;?/gi, '')
                .replace(/paint-order\s*:\s*[^;]+;?/gi, '')
                .trim()
                .replace(/;+$/, '')
            return cleaned ? ` style=${q}${cleaned}${q}` : ''
        })

        const selfClosing = closer && closer.indexOf('/') !== -1
        const strokeAttrs = normalizedStroke
            ? ` stroke="${normalizedStroke}" stroke-width="${strokeWidth}" paint-order="stroke fill"`
            : ''
        return `<${tag}${nextAttrs} fill="url(#${gradientId})"${strokeAttrs}${
            selfClosing ? ' /' : ''
        }>`
    })

    return svg
}

const GradientStopColor = ({
    color,
    value,
    onChange,
    title,
    allowEmpty = false,
}) => {
    const resolvedColor = color !== undefined ? color : value
    const validColor =
        normalizeHexColor(resolvedColor) ||
        (allowEmpty ? '' : toSwatchColor(resolvedColor))
    const [draft, setDraft] = useState(validColor || '')

    useEffect(() => {
        const next =
            normalizeHexColor(resolvedColor) ||
            (allowEmpty ? '' : toSwatchColor(resolvedColor))
        setDraft(next || '')
    }, [resolvedColor, allowEmpty])

    const commitIfValid = (raw) => {
        const trimmed = typeof raw === 'string' ? raw.trim() : ''
        if (allowEmpty && trimmed === '') {
            onChange('')
            setDraft('')
            return true
        }
        const normalized = normalizeHexColor(raw)
        if (normalized) {
            onChange(normalized)
            setDraft(normalized)
            return true
        }
        return false
    }

    return (
        <div style={{ display: 'flex' }}>
            <Input
                type="color"
                value={toSwatchColor(resolvedColor)}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                style={{
                    width: 40,
                    height: 32,
                    padding: 2,
                }}
                title={title}
            />
            <Input
                value={draft}
                placeholder={allowEmpty ? '' : '#000000'}
                maxLength={7}
                onChange={(e) => {
                    const next = e.target.value
                    setDraft(next)
                    commitIfValid(next)
                }}
                onBlur={() => {
                    if (!commitIfValid(draft)) {
                        setDraft(validColor || '')
                    }
                }}
                onPressEnter={(e) => e.target.blur()}
                style={{
                    width: 96,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
                title={title}
                aria-label={title}
            />
        </div>
    )
}

const GradientControls = ({ value, onChange, defaults }) => {
    const { t } = useTranslation()
    const parsed = parseLinearGradient(value, defaults)
    const gradientCss = buildLinearGradient(parsed)

    const commit = (next) => {
        onChange(buildLinearGradient(next))
    }

    const updateAngle = (angle) => {
        commit({
            ...parsed,
            angle: angle == null ? defaults.angle : angle,
        })
    }

    const updateStop = (index, patch) => {
        const stops = parsed.stops.map((stop, i) =>
            i === index ? { ...stop, ...patch } : stop
        )
        commit({ ...parsed, stops })
    }

    const addStop = () => {
        const stops = [...parsed.stops]
        const last = stops[stops.length - 1]
        const prev = stops[stops.length - 2] || last
        const midOffset = Math.round(
            (normalizeOffset(prev.offset) + normalizeOffset(last.offset)) / 2
        )
        stops.splice(stops.length - 1, 0, {
            color: last.color,
            offset: midOffset,
        })
        commit({ ...parsed, stops })
    }

    const removeStop = (index) => {
        if (parsed.stops.length <= 2) {
            return
        }
        const stops = parsed.stops.filter((_, i) => i !== index)
        commit({ ...parsed, stops })
    }

    return (
        <div>
            <GradientStrip gradient={gradientCss} />
            <Space
                align="center"
                wrap
                style={{ marginBottom: 8, width: '100%' }}
            >
                <Text type="secondary" style={{ marginRight: 4 }}>
                    {t('COMMUNITIES_GRADIENT_ANGLE')}
                </Text>
                <InputNumber
                    min={0}
                    max={360}
                    step={0.01}
                    value={parsed.angle}
                    onChange={updateAngle}
                    style={{ width: 100 }}
                />
                <Button type="dashed" size="small" onClick={addStop}>
                    {t('COMMUNITIES_GRADIENT_ADD_STOP')}
                </Button>
            </Space>

            <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {parsed.stops.map((stop, index) => (
                    <Space
                        key={`stop-${index}`}
                        align="center"
                        size={8}
                        wrap
                        style={{ width: '100%' }}
                    >
                        <GradientStopColor
                            color={stop.color}
                            onChange={(nextColor) =>
                                updateStop(index, { color: nextColor })
                            }
                            title={`${t('COMMUNITIES_GRADIENT_COLOR')} ${
                                index + 1
                            }`}
                        />
                        <InputNumber
                            min={0}
                            max={100}
                            step={0.01}
                            value={normalizeOffset(stop.offset)}
                            formatter={(v) => `${v}%`}
                            parser={(v) => String(v).replace('%', '')}
                            onChange={(offset) =>
                                updateStop(index, {
                                    offset:
                                        offset == null
                                            ? index === 0
                                                ? 0
                                                : 100
                                            : offset,
                                })
                            }
                            style={{ width: 90 }}
                        />
                        <Tooltip title={t('COMMUNITIES_GRADIENT_REMOVE_STOP')}>
                            <Button
                                danger
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                disabled={parsed.stops.length <= 2}
                                onClick={() => removeStop(index)}
                            />
                        </Tooltip>
                    </Space>
                ))}
            </Space>
        </div>
    )
}

const MaskPreview = ({ src, gradient, label, size }) => {
    if (!src) {
        return (
            <div
                style={{
                    width: size,
                    height: size,
                    border: '1px dashed #d9d9d9',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: 12,
                }}
            >
                {label}
            </div>
        )
    }

    return (
        <div
            title={label}
            style={{
                width: size,
                height: size,
                backgroundImage: gradient,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100% 100%',
                WebkitMaskImage: `url(${src})`,
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskImage: `url(${src})`,
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                maskSize: 'contain',
                borderRadius: 8,
            }}
        />
    )
}

const EmptyPreview = ({ label, size }) => (
    <div
        style={{
            width: size,
            height: size,
            border: '1px dashed #d9d9d9',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: 12,
        }}
    >
        {label}
    </div>
)

const SvgBakePreview = ({
    src,
    gradient,
    label,
    size,
    borderColor,
    borderPercent,
    token,
}) => {
    const [previewUrl, setPreviewUrl] = useState(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let cancelled = false
        let objectUrl = null

        const run = async () => {
            if (!src || !gradient) {
                setPreviewUrl(null)
                setFailed(false)
                return
            }

            try {
                const headers = {}
                if (token && !src.startsWith('blob:')) {
                    headers.Authorization = `Bearer ${token}`
                }
                const response = await fetch(src, { headers })
                if (!response.ok) {
                    throw new Error(`Failed to fetch SVG (${response.status})`)
                }
                const svgText = await response.text()
                const bakedSvg = applyGradientToSvg(
                    svgText,
                    gradient,
                    borderColor,
                    borderPercent
                )
                objectUrl = URL.createObjectURL(
                    new Blob([bakedSvg], { type: 'image/svg+xml' })
                )
                if (cancelled) {
                    URL.revokeObjectURL(objectUrl)
                    return
                }
                setPreviewUrl(objectUrl)
                setFailed(false)
            } catch (err) {
                if (!cancelled) {
                    setFailed(true)
                    setPreviewUrl(null)
                }
            }
        }

        run()

        return () => {
            cancelled = true
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl)
            }
        }
    }, [src, gradient, borderColor, borderPercent, token])

    if (!src) {
        return <EmptyPreview label={label} size={size} />
    }

    if (failed) {
        return (
            <MaskPreview
                src={src}
                gradient={gradient}
                label={label}
                size={size}
            />
        )
    }

    if (!previewUrl) {
        return <EmptyPreview label={label} size={size} />
    }

    return (
        <img
            src={previewUrl}
            alt={label}
            title={label}
            style={{
                width: size,
                height: size,
                objectFit: 'contain',
                display: 'block',
                borderRadius: 8,
            }}
        />
    )
}

const PreviewPair = ({
    liveSrc,
    liveGradient,
    bakedUrl,
    size,
    liveLabel = 'SVG',
    borderColor,
    borderPercent,
    liveBake = false,
    token,
}) => {
    const { t } = useTranslation()

    return (
        <Row gutter={16} align="top">
            <Col>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                    {t('COMMUNITIES_LIVE_PREVIEW')}
                </Text>
                {liveBake ? (
                    <SvgBakePreview
                        src={liveSrc}
                        gradient={liveGradient}
                        label={liveLabel}
                        size={size}
                        borderColor={borderColor}
                        borderPercent={borderPercent}
                        token={token}
                    />
                ) : (
                    <MaskPreview
                        src={liveSrc}
                        gradient={liveGradient}
                        label={liveLabel}
                        size={size}
                    />
                )}
            </Col>
            {bakedUrl && (
                <Col>
                    <Text
                        type="secondary"
                        style={{ display: 'block', marginBottom: 6 }}
                    >
                        {t('COMMUNITIES_BAKED_PREVIEW')}
                    </Text>
                    <img
                        src={bakedUrl}
                        alt="baked-preview"
                        style={{
                            width: size,
                            height: size,
                            objectFit: 'contain',
                            display: 'block',
                            borderRadius: 8,
                            background: '#fff',
                            border: '1px solid #f0f0f0',
                        }}
                    />
                </Col>
            )}
        </Row>
    )
}

const SvgUploadField = ({ value, onChange, token }) => {
    const { t } = useTranslation()

    return (
        <Upload
            name="file"
            action={`${SERVER.baseURL}${REQUEST_URI}`}
            headers={{ Authorization: `Bearer ${token}` }}
            accept=".svg,image/svg+xml"
            maxCount={1}
            listType="text"
            fileList={getUploadFileList(value)}
            onChange={(info) => onChange(info)}
        >
            <Button size="small" icon={<UploadOutlined />}>
                {t('CREATE_OFFER_UPLOAD')}
            </Button>
        </Upload>
    )
}

const ImageUploadField = ({ value, onChange, token }) => {
    const { t } = useTranslation()

    return (
        <Upload
            name="file"
            action={`${SERVER.baseURL}${REQUEST_URI}`}
            headers={{ Authorization: `Bearer ${token}` }}
            accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
            maxCount={1}
            listType="picture"
            fileList={getUploadFileList(value)}
            onChange={(info) => onChange(info)}
        >
            <Button size="small" icon={<UploadOutlined />}>
                {t('CREATE_OFFER_UPLOAD')}
            </Button>
        </Upload>
    )
}

const sectionCardStyle = {
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
}

const subSectionTitleStyle = {
    marginTop: 0,
    marginBottom: 4,
    fontSize: 14,
}

const sectionDividerStyle = { margin: '24px 0' }

const SectionTitleWithTooltip = ({ titleKey, tooltipKey, style }) => {
    const { t } = useTranslation()

    return (
        <Text strong style={{ ...subSectionTitleStyle, ...style }}>
            {t(titleKey)}
            {tooltipKey ? (
                <Tooltip title={t(tooltipKey)}>
                    <QuestionCircleOutlined
                        style={{
                            marginLeft: 6,
                            color: 'rgba(0, 0, 0, 0.45)',
                            cursor: 'help',
                        }}
                    />
                </Tooltip>
            ) : null}
        </Text>
    )
}

const QrBackgroundPreview = ({ previewUrl }) => {
    const { t } = useTranslation()
    const percent = qrOverlayPercentStyle()

    const placeholderStyle = {
        position: 'absolute',
        left: percent.left,
        top: percent.top,
        width: percent.width,
        aspectRatio: '1',
        background:
            'repeating-conic-gradient(#111 0% 25%, #fff 0% 50%) 50% / 25% 25%',
        border: '1px solid rgba(0,0,0,0.35)',
        boxShadow: '0 0 0 2px rgba(255,255,255,0.7)',
    }

    return (
        <div>
            <Text
                type="secondary"
                style={{
                    display: 'block',
                    marginBottom: 6,
                }}
            >
                {t('COMMUNITIES_ICON_QR_CODE_PREVIEW')}
            </Text>
            <div
                style={{
                    position: 'relative',
                    display: 'inline-block',
                    maxWidth: 200,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid #f0f0f0',
                    background: '#fff',
                }}
            >
                <img
                    src={previewUrl}
                    alt="icon-qr-code-preview"
                    style={{
                        width: '100%',
                        display: 'block',
                    }}
                />
                <div
                    style={placeholderStyle}
                    title={t('COMMUNITIES_ICON_QR_CODE_PLACEHOLDER')}
                />
            </div>
        </div>
    )
}

const CommunityIconEditor = () => {
    const { t } = useTranslation()
    const { token } = useContext(AppContext)

    return (
        <Card
            className="form-wrapper-card"
            title={<Title level={4}>{t('COMMUNITIES_ICON_EDITOR')}</Title>}
            style={{ height: 'fit-content', flex: 1 }}
        >
            {/* Mini-program: WeChat contact, menu icon, lottery icon, gradient color */}
            <div style={sectionCardStyle}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                    {t('COMMUNITIES_SECTION_MINIPROGRAM')}
                </Title>
                <Text type="secondary">
                    {t('COMMUNITIES_SECTION_MINIPROGRAM_HINT')}
                </Text>
                <Divider style={sectionDividerStyle} />

                <Text strong style={subSectionTitleStyle}>
                    {t('COMMUNITIES_ZIKO_CONTACT')}
                </Text>
                <Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: 8 }}
                >
                    {t('COMMUNITIES_ZIKO_CONTACT_HINT')}
                </Text>
                <Row gutter={[20, 12]}>
                    <Col xs={24} md={12}>
                        <Text strong>{t('COMMUNITIES_ZIKO_CONTACT_EN')}</Text>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item name={['zikoContact', 'en']} noStyle>
                                <Input
                                    placeholder="Add Chief Ziko on WeChat"
                                />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Text strong>{t('COMMUNITIES_ZIKO_CONTACT_CN')}</Text>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item name={['zikoContact', 'cn']} noStyle>
                                <Input
                                    placeholder={t('COMMUNITIES_ZIKO_CONTACT_CN')}
                                />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Text strong>{t('COMMUNITIES_ZIKO_CONTACT_ID')}</Text>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item name="zikoContactId" noStyle>
                                <Input
                                    placeholder={t('COMMUNITIES_ZIKO_CONTACT_ID')}
                                />
                            </Form.Item>
                        </div>
                    </Col>
                </Row>

                <Divider style={sectionDividerStyle} />

                <SectionTitleWithTooltip
                    titleKey="COMMUNITIES_SECTION_ICON"
                    tooltipKey="COMMUNITIES_SECTION_ICON_TOOLTIP"
                />
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {t('COMMUNITIES_SECTION_ICON_HINT')}
                </Text>
                <Row gutter={[20, 12]}>
                    <Col xs={24} md={12}>
                        <Text strong>{t('COMMUNITIES_ICON_SOURCE')}</Text>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item
                                name="iconSource"
                                valuePropName="value"
                                getValueFromEvent={(e) => e}
                                noStyle
                            >
                                <SvgUploadField token={token.token} />
                            </Form.Item>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Text strong>{t('COMMUNITIES_MP_ICON_STYLE')}</Text>
                            <div style={{ marginTop: 6 }}>
                                <Form.Item
                                    name={['mpConfig', 'iconStyle']}
                                    noStyle
                                >
                                    <GradientControls
                                        defaults={DEFAULT_ICON_GRADIENT}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, next) =>
                                prev.iconSource !== next.iconSource ||
                                prev.icon !== next.icon ||
                                prev.mpConfig?.iconStyle !==
                                    next.mpConfig?.iconStyle
                            }
                        >
                            {({ getFieldValue }) => {
                                const iconStyle =
                                    getFieldValue(['mpConfig', 'iconStyle']) ||
                                    buildLinearGradient(DEFAULT_ICON_GRADIENT)
                                return (
                                    <PreviewPair
                                        liveSrc={getPreviewUrl(
                                            getFieldValue('iconSource')
                                        )}
                                        liveGradient={iconStyle}
                                        bakedUrl={getPreviewUrl(
                                            getFieldValue('icon')
                                        )}
                                        size={ICON_PREVIEW_SIZE}
                                    />
                                )
                            }}
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={sectionDividerStyle} />

                <SectionTitleWithTooltip
                    titleKey="COMMUNITIES_SECTION_ICON_HEAD"
                    tooltipKey="COMMUNITIES_SECTION_ICON_HEAD_TOOLTIP"
                />
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {t('COMMUNITIES_SECTION_ICON_HEAD_HINT')}
                </Text>
                <Row gutter={[20, 12]}>
                    <Col xs={24} md={12}>
                        <Text strong>{t('COMMUNITIES_ICON_HEAD_SOURCE')}</Text>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item
                                name="iconHeadSource"
                                valuePropName="value"
                                getValueFromEvent={(e) => e}
                                noStyle
                            >
                                <SvgUploadField token={token.token} />
                            </Form.Item>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Text strong>
                                {t('COMMUNITIES_MP_ICON_HEAD_STYLE')}
                            </Text>
                            <div style={{ marginTop: 6 }}>
                                <Form.Item
                                    name={['mpConfig', 'iconHeadStyle']}
                                    noStyle
                                >
                                    <GradientControls
                                        defaults={DEFAULT_ICON_HEAD_GRADIENT}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Text strong>
                                {t('COMMUNITIES_ICON_HEAD_BORDER_COLOR')}
                            </Text>
                            <div style={{ marginTop: 6 }}>
                                <Form.Item
                                    name={['mpConfig', 'iconHeadBorderColor']}
                                    noStyle
                                >
                                    <GradientStopColor
                                        allowEmpty
                                        title={t(
                                            'COMMUNITIES_ICON_HEAD_BORDER_COLOR'
                                        )}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Text strong>
                                {t('COMMUNITIES_ICON_HEAD_BORDER_PERCENT')}
                            </Text>
                            <div style={{ marginTop: 6 }}>
                                <Form.Item
                                    name={[
                                        'mpConfig',
                                        'iconHeadBorderPercent',
                                    ]}
                                    noStyle
                                >
                                    <InputNumber
                                        min={0.1}
                                        max={20}
                                        step={0.1}
                                        addonAfter="%"
                                        style={{ width: 120 }}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, next) =>
                                prev.iconHeadSource !== next.iconHeadSource ||
                                prev.iconHead !== next.iconHead ||
                                prev.mpConfig?.iconHeadStyle !==
                                    next.mpConfig?.iconHeadStyle ||
                                prev.mpConfig?.iconHeadBorderColor !==
                                    next.mpConfig?.iconHeadBorderColor ||
                                prev.mpConfig?.iconHeadBorderPercent !==
                                    next.mpConfig?.iconHeadBorderPercent
                            }
                        >
                            {({ getFieldValue }) => {
                                const iconHeadStyle =
                                    getFieldValue([
                                        'mpConfig',
                                        'iconHeadStyle',
                                    ]) ||
                                    buildLinearGradient(
                                        DEFAULT_ICON_HEAD_GRADIENT
                                    )
                                return (
                                    <PreviewPair
                                        liveSrc={getPreviewUrl(
                                            getFieldValue('iconHeadSource')
                                        )}
                                        liveGradient={iconHeadStyle}
                                        bakedUrl={getPreviewUrl(
                                            getFieldValue('iconHead')
                                        )}
                                        size={ICON_HEAD_PREVIEW_SIZE}
                                        liveBake
                                        token={token.token}
                                        borderColor={getFieldValue([
                                            'mpConfig',
                                            'iconHeadBorderColor',
                                        ])}
                                        borderPercent={getFieldValue([
                                            'mpConfig',
                                            'iconHeadBorderPercent',
                                        ])}
                                    />
                                )
                            }}
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={sectionDividerStyle} />

                <Text strong style={subSectionTitleStyle}>
                    {t('COMMUNITIES_SECTION_TEXT')}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {t('COMMUNITIES_SECTION_TEXT_HINT')}
                </Text>
                <Row gutter={[20, 12]}>
                    <Col xs={24} md={12}>
                        <Text strong>{t('COMMUNITIES_TEXT_GRADIENT')}</Text>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item
                                name={['mpConfig', 'textGradient']}
                                noStyle
                            >
                                <GradientControls
                                    defaults={DEFAULT_TEXT_GRADIENT}
                                />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Text
                            type="secondary"
                            style={{ display: 'block', marginBottom: 6 }}
                        >
                            {t('COMMUNITIES_TEXT_PREVIEW')}
                        </Text>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, next) =>
                                prev.mpConfig?.textGradient !==
                                next.mpConfig?.textGradient
                            }
                        >
                            {({ getFieldValue }) => {
                                const textGradient =
                                    getFieldValue([
                                        'mpConfig',
                                        'textGradient',
                                    ]) ||
                                    buildLinearGradient(DEFAULT_TEXT_GRADIENT)
                                return (
                                    <div
                                        style={{
                                            display: 'inline-block',
                                            padding: '12px 16px',
                                            borderRadius: 8,
                                            background: '#fff',
                                            border: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: TEXT_PREVIEW_SIZE,
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                backgroundImage: textGradient,
                                                backgroundRepeat: 'no-repeat',
                                                WebkitBackgroundClip: 'text',
                                                backgroundClip: 'text',
                                                WebkitTextFillColor:
                                                    'transparent',
                                                color: 'transparent',
                                            }}
                                        >
                                            Ziko
                                        </div>
                                    </div>
                                )
                            }}
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            {/* H5: icon + background gradient */}
            <div style={sectionCardStyle}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                    {t('COMMUNITIES_SECTION_H5')}
                </Title>
                <Text type="secondary">
                    {t('COMMUNITIES_SECTION_H5_HINT')}
                </Text>
                <Divider style={sectionDividerStyle} />

                <Text strong style={subSectionTitleStyle}>
                    {t('COMMUNITIES_SECTION_ICON_HFIVE')}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {t('COMMUNITIES_SECTION_ICON_HFIVE_HINT')}
                </Text>
                <Row gutter={[20, 12]}>
                    <Col xs={24} md={12}>
                        <Text strong>{t('COMMUNITIES_ICON_HFIVE_SOURCE')}</Text>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item
                                name="iconHFiveSource"
                                valuePropName="value"
                                getValueFromEvent={(e) => e}
                                noStyle
                            >
                                <SvgUploadField token={token.token} />
                            </Form.Item>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Text strong>
                                {t('COMMUNITIES_MP_ICON_HFIVE_STYLE')}
                            </Text>
                            <div style={{ marginTop: 6 }}>
                                <Form.Item
                                    name={['mpConfig', 'iconHFiveStyle']}
                                    noStyle
                                >
                                    <GradientControls
                                        defaults={DEFAULT_ICON_GRADIENT}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, next) =>
                                prev.iconHFiveSource !== next.iconHFiveSource ||
                                prev.iconHFive !== next.iconHFive ||
                                prev.mpConfig?.iconHFiveStyle !==
                                    next.mpConfig?.iconHFiveStyle
                            }
                        >
                            {({ getFieldValue }) => {
                                const iconHFiveStyle =
                                    getFieldValue([
                                        'mpConfig',
                                        'iconHFiveStyle',
                                    ]) ||
                                    buildLinearGradient(DEFAULT_ICON_GRADIENT)
                                return (
                                    <PreviewPair
                                        liveSrc={getPreviewUrl(
                                            getFieldValue('iconHFiveSource')
                                        )}
                                        liveGradient={iconHFiveStyle}
                                        bakedUrl={getPreviewUrl(
                                            getFieldValue('iconHFive')
                                        )}
                                        size={ICON_PREVIEW_SIZE}
                                    />
                                )
                            }}
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={sectionDividerStyle} />

                <Text strong style={subSectionTitleStyle}>
                    {t('COMMUNITIES_BG_HFIVE')}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {t('COMMUNITIES_BG_HFIVE_HINT')}
                </Text>
                <Row gutter={[20, 12]}>
                    <Col xs={24} md={12}>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item name="bgHFive" noStyle>
                                <GradientControls
                                    defaults={DEFAULT_BG_HFIVE_GRADIENT}
                                />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Text
                            type="secondary"
                            style={{ display: 'block', marginBottom: 6 }}
                        >
                            {t('COMMUNITIES_BG_HFIVE_PREVIEW')}
                        </Text>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, next) =>
                                prev.bgHFive !== next.bgHFive
                            }
                        >
                            {({ getFieldValue }) => {
                                const bgHFive =
                                    getFieldValue('bgHFive') ||
                                    buildLinearGradient(
                                        DEFAULT_BG_HFIVE_GRADIENT
                                    )
                                return (
                                    <div
                                        style={{
                                            width: '100%',
                                            maxWidth: 280,
                                            height: 120,
                                            borderRadius: 8,
                                            backgroundImage: bgHFive,
                                            backgroundRepeat: 'no-repeat',
                                            backgroundSize: '100% 100%',
                                            border: '1px solid #f0f0f0',
                                        }}
                                    />
                                )
                            }}
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            {/* Backoffice: PDF logo + QR */}
            <div style={sectionCardStyle}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                    {t('COMMUNITIES_SECTION_BACKOFFICE')}
                </Title>
                <Text type="secondary">
                    {t('COMMUNITIES_SECTION_BACKOFFICE_HINT')}
                </Text>
                <Divider style={sectionDividerStyle} />

                <Text strong style={subSectionTitleStyle}>
                    {t('COMMUNITIES_SECTION_LOGO_PDF')}
                </Text>
                <Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: 8 }}
                >
                    {t('COMMUNITIES_SECTION_LOGO_PDF_HINT')}
                </Text>
                <Row gutter={[20, 12]}>
                    <Col xs={24} md={12}>
                        <Text strong>{t('COMMUNITIES_LOGO_PDF')}</Text>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item
                                name="logoPDF"
                                valuePropName="value"
                                getValueFromEvent={(e) => e}
                                noStyle
                            >
                                <ImageUploadField token={token.token} />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, next) =>
                                prev.logoPDF !== next.logoPDF
                            }
                        >
                            {({ getFieldValue }) => {
                                const previewUrl = getPreviewUrl(
                                    getFieldValue('logoPDF')
                                )
                                if (!previewUrl) {
                                    return null
                                }
                                return (
                                    <div>
                                        <Text
                                            type="secondary"
                                            style={{
                                                display: 'block',
                                                marginBottom: 6,
                                            }}
                                        >
                                            {t('COMMUNITIES_LIVE_PREVIEW')}
                                        </Text>
                                        <img
                                            src={previewUrl}
                                            alt="logo-pdf-preview"
                                            style={{
                                                maxWidth: 200,
                                                maxHeight: 80,
                                                objectFit: 'contain',
                                                display: 'block',
                                                borderRadius: 8,
                                                background: '#fff',
                                                border: '1px solid #f0f0f0',
                                            }}
                                        />
                                    </div>
                                )
                            }}
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={sectionDividerStyle} />

                <Text strong style={subSectionTitleStyle}>
                    {t('COMMUNITIES_ICON_QR_CODE')}
                </Text>
                <Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: 8 }}
                >
                    {t('COMMUNITIES_ICON_QR_CODE_HINT')}
                </Text>
                <Row gutter={[20, 12]}>
                    <Col xs={24} md={12}>
                        <div style={{ marginTop: 6 }}>
                            <Form.Item
                                name="iconQRCode"
                                valuePropName="value"
                                getValueFromEvent={(e) => e}
                                noStyle
                            >
                                <ImageUploadField token={token.token} />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, next) =>
                                prev.iconQRCode !== next.iconQRCode
                            }
                        >
                            {({ getFieldValue }) => {
                                const previewUrl = getPreviewUrl(
                                    getFieldValue('iconQRCode')
                                )
                                if (!previewUrl) {
                                    return null
                                }
                                return (
                                    <QrBackgroundPreview
                                        key={previewUrl}
                                        previewUrl={previewUrl}
                                    />
                                )
                            }}
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            <Form.Item name="icon" hidden>
                <Input />
            </Form.Item>
            <Form.Item name="iconHead" hidden>
                <Input />
            </Form.Item>
            <Form.Item name="iconHFive" hidden>
                <Input />
            </Form.Item>
        </Card>
    )
}

export default CommunityIconEditor
export { buildLinearGradient, parseLinearGradient, getPreviewUrl }
